/**
 * CanDo Payments, Centralised Analytics / dataLayer Utility
 *
 * Single source of truth for all GTM dataLayer pushes.
 * Every component that needs to fire an event imports from here.
 *
 * Event naming: snake_case (GA4 convention)
 * All events follow a consistent schema with page context.
 */

import {
  markPrimaryFormStarted,
  markLeadCaptured,
  markLastProductClick,
  markLastToolOpen,
  markLastPartnerClick,
  markOriginGuide,
  getOriginGuide,
  clearOriginGuide,
  getTrafficContext,
} from "@/utils/journey";
import { isAnalyticsGranted, grantAnalytics, denyAnalytics, isCampaignPageViewEligible, canUseDocumentReferrer } from "@/utils/googleTag";
import {
  GUIDE_IDS,
  GUIDE_LINK_ROLES,
  GUIDE_CTA_POSITIONS,
  GUIDE_CONTENT_GROUP,
  guideIdForPath,
} from "@/data/guidesContent";
import devLog from "@/utils/devLog";
import { startAttributionAfterConsent, clearAttributionForConsentWithdrawal } from "@/forms/engine/attribution";
import {
  isMeasurementHostAllowed,
  isMeasurementEventAllowed,
  sanitiseExternalReferrer,
  sanitiseSitePath,
  safePageContext,
  safeCampaignPageUrl,
  safeFormName,
  safeFormStepId,
  safeFormStepState,
  safeItemName,
  safeCtaId,
  safeReviewFilterName,
  safeReviewSourceName,
  safeReviewTagName,
  safeLandingEvent,
  safeWhatsAppPlacement,
  sanitiseContactEvent,
  sanitiseLinkEvent,
  sanitiseStructuredEvent,
  trackingSchemaVersion,
} from "@/utils/measurementPolicy";

// Basic consent mode: while analytics consent is unknown or denied NOTHING
// is pushed. Dropping (not queueing) matters, GTM would otherwise replay
// every denied-period event from the dataLayer the moment it loads.
function push(data) {
  if (!isAnalyticsGranted() || !isMeasurementHostAllowed()) {
    devLog("[Tracking] dropped: measurement unavailable");
    return false;
  }
  if (!isMeasurementEventAllowed(data?.event)) {
    devLog("[Tracking] rejected: event outside measurement contract");
    return false;
  }
  window.dataLayer = window.dataLayer || [];
  // GTM Data Layer Variables merge nested objects. Clear both contexts before
  // every canonical event so a later event cannot inherit a guide, form, or
  // page value that it did not explicitly provide.
  window.dataLayer.push({ event_data: null, page: null });
  window.dataLayer.push(data);
  return true;
}

function nextFrame(fn) {
  if (typeof window.requestAnimationFrame === "function") window.requestAnimationFrame(fn);
  else setTimeout(fn, 0);
}

// ─── Page-view state (per page load, in memory only) ──────────────
// lastPageViewKey: one page_view per actual navigation, collapses the
//   consent-grant page view and ScrollToTop's for the same location.
// lastPermittedLocation: sanitised origin+path of the last page view SENT
//   while consent was granted, the next page view's referrer. Never
//   updated while consent is unknown/denied and cleared on withdrawal, so
//   routes visited during refusal can't leak into a later referrer.
// sentFirstPageView: only the first page view of a page load may carry
//   document.referrer, and only when it is external (another origin).
let lastPageViewKey = "";
let lastPermittedLocation = "";
let sentFirstPageView = false;
// A finite UTM query is available only for the first returning-consented
// landing page view. It is not retained across route changes or consent
// changes, and every other event keeps a query-free page context.
let campaignPageViewAvailable = isCampaignPageViewEligible() && isMeasurementHostAllowed();
const campaignLandingPath = campaignPageViewAvailable ? safePageContext().page_path : "";

// document.referrer when it is another site (origin + path), else "". An
// internal document.referrer could be a page reached during refusal (e.g.
// a full reload/navigation while denied), so it is never used.
function externalReferrer() {
  const ref = sanitiseExternalReferrer(document.referrer);
  if (!ref) return "";
  try {
    return new URL(ref).origin === window.location.origin ? "" : ref;
  } catch (_err) {
    return ""; /* unparseable referrer */
  }
}

function renderedCanonicalPath() {
  // A rendered, stable page marker is the only permitted override to the
  // browser URL. It covers the AppRouter catch-all and the deliberate
  // invalid-guide 404 without exporting an arbitrary requested path.
  if (typeof document !== "undefined" && document.querySelector('[data-measurement-page="not_found"]')) {
    return "/404";
  }
  return "";
}

function pageContext() {
  return safePageContext(undefined, { canonicalPath: renderedCanonicalPath() });
}

function formEventData(formName, opts = {}) {
  return {
    form_name: safeFormName(formName),
    form_product: safeItemName(opts.product),
    form_source: sanitiseSitePath(opts.source || window.location.href),
    origin_guide_id: GUIDE_IDS.includes(getOriginGuide()) ? getOriginGuide() : "",
    ...pageContext(),
    tracking_schema_version: trackingSchemaVersion(),
  };
}

function safeTrafficContext() {
  const context = getTrafficContext();
  return {
    last_product_clicked: safeItemName(context.last_product_clicked),
    last_tool_opened: safeItemName(context.last_tool_opened),
    last_partner_clicked: safeItemName(context.last_partner_clicked),
  };
}

const analytics = {
  // ─── PAGE TRACKING ──────────────────────────────────────────
  // Single page_view producer (called by ScrollToTop on every navigation and
  // once on consent grant). Every key is always present, "" when not
  // applicable, because GTM's data model merges nested objects and would
  // otherwise keep a previous page's guide_id.
  pageView(path, _title) {
    // Nothing, not even navigation history, is recorded without consent.
    if (!isAnalyticsGranted() || !isMeasurementHostAllowed()) return;
    // Fragments are in-page navigation, not additional screens.
    // The route key is held in memory only to collapse duplicate component
    // mounts. It is never included in a payload, including on a 404.
    const key = typeof path === "string" && path ? path : window.location.pathname;
    if (key === lastPageViewKey) return;
    lastPageViewKey = key;
    const page = pageContext();
    let referrer = lastPermittedLocation;
    if (!referrer && !sentFirstPageView && canUseDocumentReferrer()) referrer = externalReferrer();
    lastPermittedLocation = page.page_url;
    sentFirstPageView = true;
    const guideId = page.page_path === "/404" ? "" : guideIdForPath(window.location.pathname);
    const recorded = push({
      event: "page_view",
      page: {
        url: campaignPageViewAvailable && page.page_path === campaignLandingPath
          ? safeCampaignPageUrl()
          : page.page_url,
        path: page.page_path,
        // Title values can be dynamically generated. The stable route/page
        // registry supplies report labels, so browser title is not exported.
        title: "",
        referrer,
        guide_id: guideId,
        content_group: guideId ? GUIDE_CONTENT_GROUP : "",
      },
    });
    if (recorded) campaignPageViewAvailable = false;
  },

  // ─── FORM EVENTS ───────────────────────────────────────────
  // Returns true only if form_start was actually recorded (analytics
  // granted). Callers consume their once-per-instance flag only on true, so
  // an interaction while analytics is off doesn't use up the start.
  // markPrimaryFormStarted (popup suppression) is operational and always runs.
  formStart(formName) {
    markPrimaryFormStarted(formName);
    const safeName = safeFormName(formName);
    if (!safeName) return false;
    return push({
      event: "form_start",
      event_data: formEventData(safeName),
    });
  },

  formSubmit(formName, opts = {}) {
    markPrimaryFormStarted(formName);
    const safeName = safeFormName(formName);
    if (!safeName) return false;
    // Diagnostic attempt only. It is deliberately not a lead or outcome.
    return push({
      event: "form_submit",
      event_data: { ...formEventData(safeName, opts), form_state: "attempt" },
    });
  },

  formValidationError(formName, opts = {}) {
    const safeName = safeFormName(formName);
    if (!safeName) return false;
    return push({
      event: "form_validation_error",
      event_data: { ...formEventData(safeName, opts), form_state: "validation_error" },
    });
  },

  formRequestError(formName, opts = {}) {
    const safeName = safeFormName(formName);
    if (!safeName) return false;
    return push({
      event: "form_request_error",
      event_data: { ...formEventData(safeName, opts), form_state: "request_error" },
    });
  },

  formStep(formName, state, stepId, opts = {}) {
    const safeName = safeFormName(formName);
    const safeState = safeFormStepState(state);
    const safeStep = safeFormStepId(stepId);
    if (!safeName || !safeState || !safeStep) return false;
    return push({
      event: "form_step",
      event_data: {
        ...formEventData(safeName, opts),
        form_state: safeState,
        step_id: safeStep,
      },
    });
  },

  statementTransferSuccess() {
    // This confirms browser-to-staging transfer only. It says nothing about
    // backend lead acceptance, CRM delivery, or review completion.
    return this._emitItemEvent("statement_transfer_success", "statement_review_upload", {
      itemCategory: "upload", itemType: "upload", funnelStage: "evaluation",
    });
  },

  statementTransferError() {
    // The event intentionally excludes file names, staging keys, URL, and
    // provider error content.
    return this._emitItemEvent("statement_transfer_error", "statement_review_upload", {
      itemCategory: "upload", itemType: "upload", funnelStage: "evaluation",
    });
  },

  generateLead(formName, opts = {}) {
    const safeName = safeFormName(formName);
    if (!safeName) return false;
    markLeadCaptured();
    // useLeadForm owns the per-instance submission id and lock. Do not
    // suppress another genuinely successful same-named form by a global
    // time window.
    return push({
      event: "generate_lead",
      event_data: {
        ...formEventData(safeName, opts),
        traffic_context: safeTrafficContext(),
        // Always explicit: the first guide this tab left via a CanDo link,
        // or "", so GTM never carries a previous event's origin forward.
        origin_guide_id: GUIDE_IDS.includes(getOriginGuide()) ? getOriginGuide() : "",
      },
    });
  },

  // ─── POPUP / SECONDARY JOURNEY EVENTS ──────────────────────
  // These are intentionally NOT conversions. `generate_lead` remains
  // the single primary conversion event.
  popupShown(source) {
    return push({
      event: "popup_shown",
      event_data: { form_name: "lead_popup_health_check", popup_source: sanitiseSitePath(source || window.location.href), ...pageContext(), tracking_schema_version: trackingSchemaVersion() },
    });
  },

  popupDismissed(source) {
    return push({
      event: "popup_dismissed",
      event_data: { form_name: "lead_popup_health_check", popup_source: sanitiseSitePath(source || window.location.href), ...pageContext(), tracking_schema_version: trackingSchemaVersion() },
    });
  },

  // ─── CLICK EVENTS ──────────────────────────────────────────
  clickPhone(_number, componentId = "sitewide_contact") {
    const eventData = sanitiseContactEvent("phone", componentId);
    if (!eventData) return false;
    return push({
      event: "click_phone",
      event_data: { ...eventData, destination_type: "telephone", ...pageContext(), tracking_schema_version: trackingSchemaVersion() },
    });
  },

  clickEmail(_address, componentId = "sitewide_contact") {
    const eventData = sanitiseContactEvent("email", componentId);
    if (!eventData) return false;
    return push({
      event: "click_email",
      event_data: { ...eventData, destination_type: "email", ...pageContext(), tracking_schema_version: trackingSchemaVersion() },
    });
  },

  clickCta(label, destination) {
    const ctaId = safeCtaId(label);
    const destinationPath = sanitiseSitePath(destination);
    if (!ctaId || !destinationPath) return false;
    return push({
      event: "click_cta",
      event_data: { cta_id: ctaId, destination_path: destinationPath, ...pageContext(), tracking_schema_version: trackingSchemaVersion() },
    });
  },

  clickWhatsApp(placement) {
    const safePlacement = safeWhatsAppPlacement(placement);
    if (!safePlacement) return false;
    return push({
      event: "click_whatsapp",
      event_data: {
        placement: safePlacement,
        contact_method: "whatsapp",
        destination_type: "whatsapp",
        ...pageContext(), tracking_schema_version: trackingSchemaVersion(),
      },
    });
  },

  clickNavigation(componentId, destination, linkRole = "internal_navigation") {
    const eventData = sanitiseLinkEvent({
      componentId,
      href: destination,
      linkRole,
      destinationType: linkRole === "internal_navigation" ? "internal" : linkRole === "anchor" ? "anchor" : linkRole,
    });
    if (!eventData) return false;
    return push({
      event: "click_navigation",
      event_data: { ...eventData, ...pageContext(), tracking_schema_version: trackingSchemaVersion() },
    });
  },

  // ─── CALCULATOR / TOOL EVENTS ──────────────────────────────
  emailCapture(source) {
    return push({
      event: "email_capture",
      event_data: { form_name: safeFormName(source), ...pageContext(), tracking_schema_version: trackingSchemaVersion() },
    });
  },

  calculatorResult(toolName, _resultSummary) {
    // Result values can contain merchant financial inputs. Keep only the
    // semantic tool identifier; results are not lead or revenue signals.
    return push({
      event: "calculator_result",
      event_data: { tool_name: safeItemName(toolName), ...pageContext(), tracking_schema_version: trackingSchemaVersion() },
    });
  },

  bookingSubmit(bookingType) {
    return push({
      event: "booking_submit",
      event_data: { booking_type: safeItemName(bookingType), ...pageContext(), tracking_schema_version: trackingSchemaVersion() },
    });
    // NOTE: generate_lead is fired separately by the caller via
    // analytics.generateLead(...) so it goes through markLeadCaptured()
    // and does not double-fire in GA4.
  },

  // ─── ENGAGEMENT EVENTS ─────────────────────────────────────
  scrollDepth(percent) {
    if (![25, 50, 75, 90].includes(percent)) return false;
    return push({
      event: "scroll_depth",
      event_data: { scroll_percent: percent, ...pageContext(), tracking_schema_version: trackingSchemaVersion() },
    });
  },

  viewServicePage(serviceName) {
    return push({
      event: "view_service_page",
      event_data: { service_name: safeItemName(serviceName), ...pageContext(), tracking_schema_version: trackingSchemaVersion() },
    });
  },

  // ─── CONSENT ───────────────────────────────────────────────
  // Granular per-category consent. Currently only the Analytics category
  // is user-controllable (GTM container GTM-NTPNDTF5 is GA4-only, no
  // Ads/remarketing tags). The ad_* signals stay permanently denied until
  // a Marketing category is introduced alongside the first ad tag.
  // Basic consent mode, see utils/googleTag.js for the full contract.
  // Grant: consent update queued → GTM loaded → consent_update → one
  //   page_view for the current page (never a replay of earlier pages).
  // Deny/withdraw: consent denied + GA4 hits disabled, guide attribution
  //   cleared, and nothing further is pushed.
  consentUpdate({ analytics: analyticsGranted }) {
    if (analyticsGranted) {
      const becameGranted = grantAnalytics();
      if (becameGranted) startAttributionAfterConsent();
      push({ event: "consent_update", event_data: { analytics_consent: "granted" } });
      if (becameGranted) {
        // Start fresh from the current route: no earlier referrer survives.
        lastPageViewKey = "";
        lastPermittedLocation = "";
        // Next frame, like ScrollToTop, so react-helmet-async has set the
        // title; whichever runs first sends, the other is deduped.
        nextFrame(() => analytics.pageView(window.location.pathname, document.title));
      }
    } else {
      denyAnalytics();
      campaignPageViewAvailable = false;
      clearOriginGuide();
      clearAttributionForConsentWithdrawal();
      lastPageViewKey = "";
      lastPermittedLocation = "";
    }
  },

  // ─── STRUCTURED JOURNEY EVENTS ─────────────────────────────
  // Shared payload builder. Every structured event has the same shape
  // so GTM/GA4 can map once and report consistently.
  // opts: { itemCategory, itemType, source, submissionMethod, funnelStage, extra }
  _emitItemEvent(eventName, itemName, opts = {}) {
    const payload = {
      event: eventName,
      event_data: { ...sanitiseStructuredEvent({ itemName, ...opts }), traffic_context: safeTrafficContext() },
    };
    const recorded = push(payload);
    devLog("[Tracking] structured event emitted");
    return recorded;
  },

  // Product / service tracking
  productViewed(itemName, opts = {}) {
    return this._emitItemEvent("product_viewed", itemName, {
      itemCategory: "product", itemType: opts.itemType || "service_product",
      funnelStage: "interest", ...opts,
    });
  },
  productClicked(itemName, opts = {}) {
    const recorded = this._emitItemEvent("product_clicked", itemName, {
      itemCategory: "product", itemType: opts.itemType || "service_product",
      funnelStage: "interest", ...opts,
    });
    if (recorded) markLastProductClick(safeItemName(itemName));
    return recorded;
  },

  // Tool / calculator tracking
  toolOpened(itemName, opts = {}) {
    const recorded = this._emitItemEvent("tool_opened", itemName, {
      itemCategory: "tool", itemType: opts.itemType || "calculator",
      funnelStage: "interest", ...opts,
    });
    if (recorded) markLastToolOpen(safeItemName(itemName));
    return recorded;
  },
  toolStarted(itemName, opts = {}) {
    return this._emitItemEvent("tool_started", itemName, {
      itemCategory: "tool", itemType: opts.itemType || "calculator",
      funnelStage: "evaluation", ...opts,
    });
  },
  toolCompleted(itemName, opts = {}) {
    return this._emitItemEvent("tool_completed", itemName, {
      itemCategory: "tool", itemType: opts.itemType || "calculator",
      funnelStage: "evaluation", ...opts,
    });
  },

  // Quote / email / upload / lead tracking
  quoteRequested(itemName, opts = {}) {
    this._emitItemEvent("quote_requested", itemName, {
      itemCategory: "quote", itemType: opts.itemType || "quote_tool",
      submissionMethod: opts.submissionMethod || "form",
      funnelStage: "conversion", ...opts,
    });
  },
  emailQuoteRequested(itemName, opts = {}) {
    this._emitItemEvent("email_quote_requested", itemName, {
      itemCategory: "quote", itemType: opts.itemType || "quote_tool",
      submissionMethod: "email",
      funnelStage: "conversion", ...opts,
    });
  },
  statementFileSelected(itemName, opts = {}) {
    // File selection is not upload, transfer, CRM delivery or lead success.
    return this._emitItemEvent("statement_file_selected", itemName || "statement_review_upload", {
      itemCategory: "upload", itemType: opts.itemType || "upload",
      funnelStage: "evaluation", ...opts,
    });
  },
  leadSubmitted(itemName, opts = {}) {
    this._emitItemEvent("lead_submitted", itemName, {
      itemCategory: opts.itemCategory || "tool", itemType: opts.itemType || "",
      submissionMethod: opts.submissionMethod || "form",
      funnelStage: "conversion", ...opts,
    });
  },

  // Partner / Work With Us tracking
  partnerSectionViewed(itemName = "work_with_us_section", opts = {}) {
    return this._emitItemEvent("partner_section_viewed", itemName, {
      itemCategory: "partner", itemType: opts.itemType || "partner_service",
      funnelStage: "interest", ...opts,
    });
  },
  partnerClicked(itemName, opts = {}) {
    const recorded = this._emitItemEvent("partner_clicked", itemName, {
      itemCategory: "partner", itemType: opts.itemType || "partner_service",
      funnelStage: "interest", ...opts,
    });
    if (recorded) markLastPartnerClick(safeItemName(itemName));
    return recorded;
  },
  partnerToolOpened(itemName, opts = {}) {
    const recorded = this._emitItemEvent("partner_tool_opened", itemName, {
      itemCategory: "partner", itemType: opts.itemType || "partner_calculator",
      funnelStage: "interest", ...opts,
    });
    if (recorded) markLastPartnerClick(safeItemName(itemName));
    return recorded;
  },
  partnerToolStarted(itemName, opts = {}) {
    return this._emitItemEvent("partner_tool_started", itemName, {
      itemCategory: "partner", itemType: opts.itemType || "partner_calculator",
      funnelStage: "evaluation", ...opts,
    });
  },
  partnerToolCompleted(itemName, opts = {}) {
    return this._emitItemEvent("partner_tool_completed", itemName, {
      itemCategory: "partner", itemType: opts.itemType || "partner_calculator",
      funnelStage: "evaluation", ...opts,
    });
  },
  partnerLeadStarted(itemName, opts = {}) {
    return this._emitItemEvent("partner_lead_started", itemName, {
      itemCategory: "partner", itemType: opts.itemType || "partner_programme",
      submissionMethod: "form",
      funnelStage: "evaluation", ...opts,
    });
  },
  partnerLeadSubmitted(itemName, opts = {}) {
    return this._emitItemEvent("partner_lead_submitted", itemName, {
      itemCategory: "partner", itemType: opts.itemType || "partner_programme",
      submissionMethod: opts.submissionMethod || "form",
      funnelStage: "conversion", ...opts,
    });
  },

  // ─── COMPARISON GUIDES (contract comparison-guides-v1) ──────
  // One event per genuine link activation on a /guides/* page. Parameters
  // are the contract's controlled values only, never link text or form
  // data. A click to a CanDo page (not a provider source) records the guide
  // for the session so a later generate_lead carries origin_guide_id. The
  // forms themselves are untouched.
  guideLinkClick(guideId, linkRole, ctaPosition) {
    // Fail closed: every value must be in its approved set (type included).
    // No event and no fallback to an earlier value otherwise.
    if (
      !GUIDE_IDS.includes(guideId) ||
      !GUIDE_LINK_ROLES.includes(linkRole) ||
      !GUIDE_CTA_POSITIONS.includes(ctaPosition)
    ) {
      devLog("[Tracking] guide_link_click rejected");
      return;
    }
    const recorded = push({
      event: "guide_link_click",
      event_data: {
        guide_id: guideId,
        link_role: linkRole,
        cta_position: ctaPosition,
        ...pageContext(), tracking_schema_version: trackingSchemaVersion(),
      },
    });
    if (recorded && linkRole !== "provider_source") markOriginGuide(guideId);
    devLog("[Tracking] guide_link_click emitted");
  },

  // ─── PPC LANDING PAGES ─────────────────────────────────────
  // Thin, generic emitter for the hidden PPC landing pages (e.g.
  // /landing/partner-programme). The page passes the exact spec event
  // name (partner_apply_click, partner_form_submit, partner_scroll_50…)
  // and any extra event_data. Uses the same push()/pageContext() path as
  // every other event so the dataLayer stays the single source of truth,
  // no new infrastructure, just one more named method on this object.
  landingEvent(eventName, extra = {}) {
    const safeEvent = safeLandingEvent(eventName);
    if (!safeEvent) return false;
    const eventData = { ...pageContext(), tracking_schema_version: trackingSchemaVersion() };
    // Landing forms distinguish only these fixed failure states. Do not add
    // browser errors, slider values, labels, URLs or arbitrary properties.
    if (["validation", "request"].includes(extra.reason)) eventData.reason = extra.reason;
    const recorded = push({
      event: safeEvent,
      event_data: eventData,
    });
    devLog("[Tracking] landing:", safeEvent);
    return recorded;
  },

  // Reviews page, lightweight interaction events for CRO analysis
  reviewFilterSelected(source, opts = {}) {
    const itemName = safeReviewFilterName(source);
    if (!itemName) return false;
    return this._emitItemEvent("review_filter_selected", itemName, {
      itemCategory: "social_proof", itemType: "review_filter",
      funnelStage: "interest", ...opts,
    });
  },
  reviewCardClicked(source, opts = {}) {
    const itemName = safeReviewSourceName(source);
    if (!itemName) return false;
    return this._emitItemEvent("review_card_clicked", itemName, {
      itemCategory: "social_proof", itemType: "review_card",
      funnelStage: "interest", ...opts,
    });
  },
  reviewTagClicked(type, value, opts = {}) {
    const itemName = safeReviewTagName(type, value);
    if (!itemName) return false;
    return this._emitItemEvent("review_tag_clicked", itemName, {
      itemCategory: "social_proof", itemType: "review_tag",
      funnelStage: "consideration", ...opts,
    });
  },
};

export default analytics;
